import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useAsyncResource } from '../hooks/useAsyncResource'
import { foundersAPI, settingsAPI } from '../services/api'
import FeaturedRepositories from '../components/FeaturedRepositories'
import LoginForm from '../components/LoginForm'
import SignupForm from '../components/SignupForm'
import CertifiedBadge from '../components/CertifiedBadge'
import './Home.css'

function Home() {
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState('login')
  const [registrationOpen, setRegistrationOpen] = useState(true)
  const [foundersSummary, setFoundersSummary] = useState({
    founding_count: 0,
    founding_cap: 100,
    program_open: true,
  })
  const [foundersSort, setFoundersSort] = useState('most_contributions')
  const [secondaryLineIndex, setSecondaryLineIndex] = useState(0)

  const secondaryLines = [
    'Become a Founding Contributor - publish your first schematic',
    'Claim your spot in the first 100 engineers',
    'Secure your Founding Badge before slots fill',
  ]

  useEffect(() => {
    settingsAPI.get()
      .then((data) => setRegistrationOpen(data?.registration_open !== false))
      .catch(() => setRegistrationOpen(true))
  }, [])

  useEffect(() => {
    foundersAPI.get({ limit: 1 })
      .then((data) => {
        setFoundersSummary({
          founding_count: Number(data?.founding_count || 0),
          founding_cap: Number(data?.founding_cap || 100),
          program_open: data?.program_open !== false,
        })
      })
      .catch(() => {
        setFoundersSummary({
          founding_count: 0,
          founding_cap: 100,
          program_open: true,
        })
      })
  }, [])

  const { data: wallData, loading: foundersWallLoading } = useAsyncResource(
    (signal) => foundersAPI.get({ sort: foundersSort, limit: 12, offset: 0, signal }),
    [foundersSort]
  )
  const foundersWall = Array.isArray(wallData?.founders) ? wallData.founders : []
  const foundersTotal = Number(wallData?.total || 0)

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setSecondaryLineIndex((current) => (current + 1) % secondaryLines.length)
    }, 4000)
    return () => window.clearInterval(intervalId)
  }, [secondaryLines.length])

  const progressPercent = Math.min(
    100,
    Math.round((foundersSummary.founding_count / foundersSummary.founding_cap) * 100)
  )
  // Check if we should show login form (from redirect)
  useEffect(() => {
    if (location.state?.showLogin && !isAuthenticated) {
      setActiveTab('login')
      // Scroll to top and to auth form
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => {
        const authForm = document.querySelector('.auth-form-container')
        if (authForm) {
          authForm.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }
  }, [location.state, isAuthenticated])

  // If user is already logged in, show their info instead of auth form
  if (isAuthenticated && user) {
    return (
      <div className="home-page">
        <div className="hero">
          <div className="badge">
            <span className="badge-dot"></span>
            <span>Welcome Back</span>
          </div>
          <h1 className="title">
            <span className="title-line">Hello,</span>
            <span className="title-line title-accent">{user.username}</span>
          </h1>
          <p className="description">
            Ready to contribute to the open source electronics reverse-engineering community?
          </p>
          <div className="cta-buttons">
            <Link to="/browse" className="btn btn-primary">
              Browse Repositories
            </Link>
            <Link to="/dashboard" className="btn btn-secondary">
              Go to Dashboard
            </Link>
          </div>
        </div>
        <FeaturedRepositories />
      </div>
    )
  }

  return (
    <div className="home-page">
      <div className="hero">
        <div className="hero-globe-container">
          <svg className="hero-globe" viewBox="0 0 300 300" preserveAspectRatio="xMidYMid meet">
            {/* Globe base circle */}
            <circle cx="150" cy="150" r="110" fill="none" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3"/>
            
            {/* Latitude lines */}
            <ellipse cx="150" cy="150" rx="110" ry="55" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            <ellipse cx="150" cy="120" rx="95" ry="48" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            <ellipse cx="150" cy="180" rx="95" ry="48" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            
            {/* Longitude lines */}
            <path d="M 150 40 Q 150 150, 150 260" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            <path d="M 40 150 Q 150 150, 260 150" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            <path d="M 75 60 Q 150 150, 225 240" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            <path d="M 225 60 Q 150 150, 75 240" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22"/>
            
            {/* Simplified connection nodes */}
            <circle cx="110" cy="110" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="190" cy="110" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="110" cy="190" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.2s" repeatCount="indefinite"/>
            </circle>
            <circle cx="190" cy="190" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.8s" repeatCount="indefinite"/>
            </circle>
            
            {/* Central hub node */}
            <circle cx="150" cy="150" r="5" fill="var(--accent)" opacity="0.8" className="globe-node hub">
              <animate attributeName="opacity" values="0.6;0.9;0.6" dur="2s" repeatCount="indefinite"/>
              <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite"/>
            </circle>
            
            {/* Simple connection lines */}
            <line x1="150" y1="150" x2="110" y2="110" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.5s" repeatCount="indefinite"/>
            </line>
            <line x1="150" y1="150" x2="190" y2="110" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.7s" repeatCount="indefinite"/>
            </line>
            <line x1="150" y1="150" x2="110" y2="190" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.6s" repeatCount="indefinite"/>
            </line>
            <line x1="150" y1="150" x2="190" y2="190" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.4s" repeatCount="indefinite"/>
            </line>
          </svg>
        </div>
        
        <div className="badge">
          <span className="badge-dot"></span>
          <span>Founders Program</span>
        </div>
        
        <h1 className="title">
          <span className="title-line">Open Source</span>
          <span className="title-line title-accent">Circuit Schematics</span>
          <span className="title-line">Community</span>
        </h1>

        <p className="description">
          Join the first 100 engineers helping co-architect the open source reverse-engineering
          forum for circuit schematics. Preserve. Document. Share.
        </p>

        <div className="founding-urgency">
          <div className="founding-urgency-header">
            <span className={`program-status ${foundersSummary.program_open ? 'open' : 'closed'}`}>
              {foundersSummary.program_open ? 'Program Open' : 'Program Closed'}
            </span>
            <span className="program-count">
              {foundersSummary.founding_count}/{foundersSummary.founding_cap}
            </span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <p className="program-note">
            Program closes when the first 100 engineers qualify.
          </p>
        </div>

        <div className="cta-buttons">
          <Link to="/founders" className="btn btn-primary">
            About the Program
          </Link>
        </div>

        <p className="secondary-cta-line">{secondaryLines[secondaryLineIndex]}</p>
      </div>

      <div className="visual-section">
        <div className="auth-preview">
          <div className="preview-content">
            <div className="auth-form-container">
              {location.state?.showLogin && (
                <div className="login-prompt-message">
                  <p>Sign in to create a repository</p>
                </div>
              )}
              {!registrationOpen && (
                <div className="registration-closed-message">
                  <p>Registration is currently closed. You can still log in if you have an account.</p>
                </div>
              )}
              <div className="auth-tabs">
                <button
                  className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                  onClick={() => setActiveTab('login')}
                >
                  Login
                </button>
                <button
                  className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
                  onClick={() => registrationOpen && setActiveTab('signup')}
                  disabled={!registrationOpen}
                  title={!registrationOpen ? 'Registration is currently closed' : ''}
                >
                  Join
                </button>
              </div>
              
              {activeTab === 'login' ? (
                <LoginForm onSwitchToSignup={() => registrationOpen && setActiveTab('signup')} />
              ) : registrationOpen ? (
                <SignupForm onSwitchToLogin={() => setActiveTab('login')} />
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '1rem' }}>Registration is closed. Switch to Login to sign in.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="founders-wall-section">
        <div className="founders-wall-header">
          <div>
            <h2 className="founders-wall-title">Founding Engineers</h2>
            <p className="founders-wall-subtitle">First 100 Contributors</p>
          </div>
          <div className="founders-wall-controls">
            <label htmlFor="founders-sort" className="founders-sort-label">Sort</label>
            <select
              id="founders-sort"
              value={foundersSort}
              onChange={(e) => setFoundersSort(e.target.value)}
              className="founders-sort-select"
            >
              <option value="most_contributions">Most contributions</option>
              <option value="newest_founders">Newest founders</option>
            </select>
          </div>
        </div>

        {foundersWallLoading ? (
          <p className="founders-wall-empty">Loading founders…</p>
        ) : foundersWall.length === 0 ? (
          <p className="founders-wall-empty">No founders have qualified yet.</p>
        ) : (
          <div className="founders-wall-grid">
            {foundersWall.map((founder) => (
              <div key={founder.user_id} className="founder-card">
                <div className="founder-avatar-wrap">
                  {founder.avatar_url ? (
                    <img
                      src={founder.avatar_url}
                      alt={`${founder.username} avatar`}
                      className="founder-avatar"
                    />
                  ) : (
                    <div className="founder-avatar founder-avatar-fallback">
                      {(founder.username || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="founder-meta">
                  <p className="founder-username">{founder.username}</p>
                  <CertifiedBadge
                    user={{
                      is_founding_contributor: true,
                    }}
                  />
                  <p className="founder-contribs">{founder.approved_contributions} approved contributions</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="founders-wall-total">
          {foundersTotal} founders listed. <Link to="/founders">View full wall</Link>
        </p>
      </section>

      <FeaturedRepositories />
    </div>
  )
}

export default Home

